import isEmpty from 'lodash/fp/isEmpty';
import some from 'lodash/fp/some';
import type { IntlShape } from 'react-intl';
import type { Dispatch } from 'redux';

import { config } from '../config';
import { getLocale } from '../configuration/lang/langSlice';
import { isUserSessionExpired } from '../configuration/login/loginSlice';
import { featureToggles } from '../configuration/setup/featureToggles';
import { fetchData } from '../configuration/setup/fetch';
import type { RootState } from '../configuration/setup/store';
import { getAccessToken } from '../configuration/tokenHandling/tokenSlice';
import { transformData } from '../data/dataDefinition';
import { getAssets, getDrivers, getDrivingTimes, getGeofences, getGroups } from '../features/app/selectors';
import { actions } from './actions';
import { buildFetchResult, type CustomResponse, Status } from './buildFetchResult';
import { fetchPlatformNotifications } from './fetchPlatformNotifications';
import { mapAssetList } from './mapper/mapAssetList';
import { type CombinedApiData, mapAssetsAndDriversWithStates } from './mapper/mapAssetsAndDriversWithStates';
import { mapDriversList } from './mapper/mapDrivers';
import { mapAssetLiveStates, mapDriverTimes } from './mapper/mapStates';
import {
    type Asset,
    type Driver,
    type DrivingTimes,
    type DrivingTimesList,
    type GeoBookingState,
    GeoBookingStates,
    type Geofence,
    type Group,
    type LiveState,
    type TransformedData,
} from './types';

const fetchThen =
    // biome-ignore lint/suspicious/noExplicitAny: We don't know the type
        <T>(mapFunction: (response: any) => any) =>
        async (uri: string, token: string) => {
            try {
                const response = await fetchData(uri, token);
                const data = mapFunction(response);
                return buildFetchResult<T>(Status.FETCH_SUCCESS, data, null);
            } catch (error) {
                return buildFetchResult<T>(Status.FETCH_FAILURE, [] as T, error as Error);
            }
        };

const fetchAssetLiveState = (token: string, locale?: string) => {
    const assetLiveStateUri = `${config.backend.ASSET_LIVE_STATE_SERVICE}/assets?locale=${locale}`;
    return fetchThen<LiveState[]>(mapAssetLiveStates)(assetLiveStateUri, token);
};

const getOrFetchDrivingTimes = async (
    token: string,
    state: RootState,
    forceFetch = false,
    after?: string
): Promise<CustomResponse<DrivingTimes[]>> => {
    if (!forceFetch) {
        const drivingTimes = getDrivingTimes(state) || [];
        if (Array.isArray(drivingTimes) && drivingTimes.length !== 0) {
            return buildFetchResult(Status.FETCH_SUCCESS, drivingTimes, null);
        }
    }

    const drivingTimesUri = `${config.backend.DRIVING_TIMES_SERVICE}/drivers${after ? `?after=${after}` : ''}`;
    const drivingTimesResponse = await fetchThen<DrivingTimesList>(mapDriverTimes)(drivingTimesUri, token);

    if (drivingTimesResponse.data?.after) {
        const moreDrivingTimes = await getOrFetchDrivingTimes(token, state, true, drivingTimesResponse.data.after);
        const items = moreDrivingTimes.data || [];
        return buildFetchResult(Status.FETCH_SUCCESS, [...drivingTimesResponse.data.items, ...items], null);
    }
    return buildFetchResult(Status.FETCH_SUCCESS, drivingTimesResponse.data?.items || [], null);
};

const getOrFetchAssets = (token: string, state: RootState, forceFetch = false) => {
    if (!forceFetch) {
        const assets = getAssets(state) || [];
        if (Array.isArray(assets) && assets.length !== 0) {
            return buildFetchResult<Asset[]>(Status.FETCH_SUCCESS, assets, null);
        }
    }
    const assetListUri = `${config.backend.LIVEMONITOR_SERVICE}/states/assets`;
    return fetchThen<Asset[]>(mapAssetList)(assetListUri, token);
};

const getOrFetchDrivers = (token: string, state: RootState, forceFetch = false) => {
    if (!forceFetch) {
        const drivers = getDrivers(state) || [];
        if (Array.isArray(drivers) && drivers.length !== 0) {
            return buildFetchResult<Driver[]>(Status.FETCH_SUCCESS, drivers, null);
        }
    }

    const driverListUri = `${config.backend.LIVEMONITOR_SERVICE}/states/drivers`;
    return fetchThen<Driver[]>(mapDriversList)(driverListUri, token);
};

const getOrFetchGeofences = async (token: string, state: RootState, dispatch: Dispatch, forceFetchFromApi = false) => {
    if (!forceFetchFromApi) {
        const geofences = getGeofences(state) || [];
        // Always return result from state, even if it is empty:
        // Many accounts do not have any geofences defined, so the geofences array in the state is likely to be empty,
        // so fetching from the API when the state array is empty would render the forceFetch parameter meaningless.
        if (Array.isArray(geofences)) {
            return await buildFetchResult<Geofence[]>(Status.FETCH_SUCCESS, geofences, null);
        }
    }

    try {
        dispatch(actions.fetchGeofencesRequested());
        const geofencesUri = `${config.backend.LIVEMONITOR_SERVICE}/geofences`;
        const result = await fetchThen<Geofence[]>(result => result.items)(geofencesUri, token);
        dispatch(actions.fetchGeofencesSuccess(result.data ?? []));
        return result;
    } catch (error: unknown) {
        dispatch(actions.fetchGeofencesFailed(error as Error));
        return await buildFetchResult(Status.FETCH_FAILURE, [], null);
    }
};

export const UNGROUPED_ITEMS_GROUP_ID = 'unassigned';

export const getUngroupedGroup = (intl: IntlShape) => ({
    id: UNGROUPED_ITEMS_GROUP_ID,
    name: intl.formatMessage({ id: 'intl-msg:tree.group.ungrouped' }),
    position: 'last',
});

const getOrFetchGroups = async (
    token: string,
    intl: IntlShape,
    state: RootState,
    dispatch: Dispatch,
    forceFetchFromApi = false
) => {
    if (!forceFetchFromApi) {
        const groups = getGroups(state) || [];
        // Always return result from state, even if it is empty:
        // Many accounts do not have any groups defined, so the groups array in the state is likely to be empty,
        // so fetching from the API when the state array is empty would render the forceFetch parameter meaningless.
        if (Array.isArray(groups)) {
            return await buildFetchResult<Group[]>(Status.FETCH_SUCCESS, groups, null);
        }
    }

    try {
        dispatch(actions.fetchGroupsRequested());

        const groupsUrl = `${config.backend.LIVEMONITOR_SERVICE}/states/groups`;
        const groupList = await fetchThen<Group[]>(result => result.items)(groupsUrl, token);
        const groups = groupList.data;

        // dont add an unassigned group if there are no other groups -> flat list of vehicles
        const groupsWithVirtualGroups: Group[] = !groups || isEmpty(groups) ? [] : [...groups, getUngroupedGroup(intl)];

        dispatch(actions.groupsChanged(groupsWithVirtualGroups));
        dispatch(actions.fetchGroupsSuccess());
        return { ...groupList, data: groupsWithVirtualGroups };
    } catch (error: unknown) {
        dispatch(actions.fetchGroupsFailed(error as Error));
        return await buildFetchResult(Status.FETCH_FAILURE, [], null);
    }
};

export const findHasAnyGeoBooked = some((asset: Asset) => asset.geoBookingState === GeoBookingStates.active);
export const findHasAnyGeoPending = some((asset: Asset) => asset.geoBookingState === GeoBookingStates.pending);
export const getOverallGeoBookingState = (transformedData: TransformedData[]): GeoBookingState => {
    if (findHasAnyGeoBooked(transformedData)) {
        return GeoBookingStates.active;
    }
    if (findHasAnyGeoPending(transformedData)) {
        return GeoBookingStates.pending;
    }
    return GeoBookingStates.inactive;
};

let callCounterAdministration = 0;
let callCounterDrivingTimes = 0;
let callCounterGeofences = 0;
let callCounterGroups = 0;

type CustomResponseCombinedApiData = {
    [key in keyof CombinedApiData]: CustomResponse<CombinedApiData[key]>;
};

export const fetchAssetsAndDriversWithStates =
    (intl: IntlShape, forceFetch?: boolean) => async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const token = getAccessToken(state);
        const locale = getLocale(state);

        if (isUserSessionExpired(state)) {
            console.warn('Session expired');
            return;
        }

        const forceFetchAdministration = callCounterAdministration === 0 || !!forceFetch;
        const forceFetchDrivingTimes = callCounterDrivingTimes === 0;
        const forceFetchGeofences = callCounterGeofences === 0 || !!forceFetch;
        const forceFetchGroups = callCounterGroups === 0 || !!forceFetch;

        try {
            const assets = await getOrFetchAssets(token, state, forceFetchAdministration);
            if (assets.status !== Status.FETCH_SUCCESS) {
                throw assets.error;
            }

            const [liveStates, drivingTimes, drivers, platformNotifications, geofences, groups] = await Promise.all([
                fetchAssetLiveState(token, locale),
                getOrFetchDrivingTimes(token, state, forceFetchDrivingTimes),
                getOrFetchDrivers(token, state, forceFetchAdministration),
                fetchPlatformNotifications(token),
                getOrFetchGeofences(token, state, dispatch, forceFetchGeofences),
                getOrFetchGroups(token, intl, state, dispatch, forceFetchGroups),
            ]);

            const responses: CustomResponseCombinedApiData = {
                liveStates,
                drivers,
                drivingTimes,
                platformNotifications,
                assets,
                geofences,
                groups,
            };

            const data = toApiData(responses);

            const mappedAssetsAndDriversWithStates = mapAssetsAndDriversWithStates(data);

            dispatch(actions.assetsChanged(data.assets));
            dispatch(actions.driversChanged(data.drivers));
            dispatch(actions.drivingTimesChanged(data.drivingTimes));

            dispatch(actions.rawDataChanged(mappedAssetsAndDriversWithStates));

            const transformedData = transformData(mappedAssetsAndDriversWithStates, intl);
            const overallGeoBookingState = getOverallGeoBookingState(transformedData);

            dispatch(actions.transformedDataChanged(transformedData));
            dispatch(
                actions.overallGeoBookingStateChanged(
                    featureToggles.simulateGeoBooked ? GeoBookingStates.active : overallGeoBookingState
                )
            );

            // TODO Decide how often to updates assets and drivers, now 15*20s /60s = every 5 minutes
            callCounterAdministration = (callCounterAdministration + 1) % 15;
            callCounterDrivingTimes = (callCounterDrivingTimes + 1) % 3;
            callCounterGeofences = (callCounterGeofences + 1) % 15;
            callCounterGroups = (callCounterGroups + 1) % 15;
        } catch (error: unknown) {
            if (!error) {
                return;
            }

            const err = error as Error;

            if (err.message.startsWith('401')) {
                // For now silently log unauthenticated errors to the console as the SessionExpiredDialog will
                // be shown instead. No need to render a dedicated error message.
                console.info('Could not fetch data. Please log in.');
            } else {
                dispatch(actions.fetchInitialDataFailed(err));
            }
        }
    };

const toApiData = (response: CustomResponseCombinedApiData): CombinedApiData => ({
    liveStates: response.liveStates.data || [],
    drivingTimes: response.drivingTimes.data || [],
    assets: response.assets.data || [],
    drivers: response.drivers.data || [],
    platformNotifications: response.platformNotifications.data || [],
    geofences: response.geofences.data || [],
    groups: response.groups.data || [],
});
